import React from "react";
import PropTypes from "prop-types";
import { FileSpreadsheet, Stethoscope } from "lucide-react";

import Card from "../../../../components/Card";
import { UIPageHeader, UIStatusTag } from "../../../../components/ui";
import { clx } from "../../../../utils/Helpers";

export const ProfileSectionCard = ({ title, children, className }) => (
  <Card
    title={title}
    classNames={{
      root: clx("company-applicant-profile__card", className),
      body: "company-applicant-profile__card-body",
    }}
  >
    {children}
  </Card>
);

export const ProfileSubsection = ({
  title,
  children,
  divider = true,
  grid = true,
}) => (
  <section
    className={clx(
      "company-applicant-profile__section",
      divider && "company-applicant-profile__section--divider",
    )}
  >
    {title ? (
      <h3 className="company-applicant-profile__section-title">{title}</h3>
    ) : null}
    {grid ? (
      <div className="company-applicant-profile__field-grid">{children}</div>
    ) : (
      children
    )}
  </section>
);

export const HealthBlock = ({ title, label, value }) => (
  <div className="company-applicant-profile__health-block">
    <h4 className="company-applicant-profile__health-title">{title}</h4>
    <div className="company-applicant-profile__health-detail">
      <div className="company-applicant-profile__bank-icon-box" aria-hidden>
        <Stethoscope size={18} strokeWidth={1.75} />
      </div>
      <div className="company-applicant-profile__bank-copy">
        <span className="company-applicant-profile__meta-label">{label}</span>
        <span className="company-applicant-profile__meta-value">{value}</span>
      </div>
    </div>
  </div>
);

export const CompanyWorkedWithRow = ({ label, children }) => (
  <div className="company-applicant-profile__company-box">
    <div className="company-applicant-profile__bank-icon-box" aria-hidden>
      <FileSpreadsheet size={18} strokeWidth={1.5} />
    </div>
    <div className="company-applicant-profile__company-copy">
      <span className="company-applicant-profile__company-label">{label}</span>
      {children}
    </div>
  </div>
);

export const ApplicantProfileHeader = ({
  actions,
  avatar,
  copy,
  headerStats,
  isAssigned,
  isVerified,
  onEventsClick,
  subtitle,
  title,
}) => (
  <UIPageHeader
    className="company-applicant-profile__hero"
    actions={actions}
    avatar={avatar}
    badges={
      isAssigned ? (
        <UIStatusTag preset="header" status="assigned" label={copy.assigned} />
      ) : null
    }
    status={isVerified ? "verified" : "unverified"}
    statusLabel={isVerified ? copy.verified : copy.unverified}
    subtitle={subtitle}
    title={title}
  >
    <div className="company-applicant-profile__header-stats">
      <div
        className={clx(
          "company-applicant-profile__header-stat",
          onEventsClick && "company-applicant-profile__header-stat--clickable",
        )}
        onClick={onEventsClick}
      >
        <span className="company-applicant-profile__header-stat-value">
          {headerStats.eventsCount}
        </span>
        <span className="company-applicant-profile__header-stat-label">
          {headerStats.eventsLabel}
        </span>
      </div>
      <div className="company-applicant-profile__header-stat">
        <span className="company-applicant-profile__header-stat-value">
          {headerStats.workingHours}
        </span>
        <span className="company-applicant-profile__header-stat-label">
          {copy.workingHours}
        </span>
      </div>
      {headerStats.attendanceValue != null ? (
        <div className="company-applicant-profile__header-stat">
          <span className="company-applicant-profile__header-stat-value">
            {headerStats.attendanceValue}%
          </span>
          <span className="company-applicant-profile__header-stat-label">
            {copy.attendance}
          </span>
        </div>
      ) : null}
    </div>
  </UIPageHeader>
);

ProfileSubsection.propTypes = {
  title: PropTypes.node,
  children: PropTypes.node.isRequired,
  divider: PropTypes.bool,
  grid: PropTypes.bool,
};

ProfileSubsection.defaultProps = {
  title: null,
  divider: true,
  grid: true,
};

ProfileSectionCard.propTypes = {
  title: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

ProfileSectionCard.defaultProps = {
  className: "",
};

CompanyWorkedWithRow.propTypes = {
  label: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
};

HealthBlock.propTypes = {
  title: PropTypes.node.isRequired,
  label: PropTypes.node.isRequired,
  value: PropTypes.node.isRequired,
};

ApplicantProfileHeader.propTypes = {
  actions: PropTypes.node,
  avatar: PropTypes.object.isRequired,
  copy: PropTypes.object.isRequired,
  headerStats: PropTypes.shape({
    attendanceValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    eventsCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
      .isRequired,
    eventsLabel: PropTypes.node.isRequired,
    workingHours: PropTypes.node.isRequired,
  }).isRequired,
  isAssigned: PropTypes.bool,
  isVerified: PropTypes.bool.isRequired,
  onEventsClick: PropTypes.func,
  subtitle: PropTypes.arrayOf(PropTypes.node).isRequired,
  title: PropTypes.node.isRequired,
};

ApplicantProfileHeader.defaultProps = {
  actions: null,
  isAssigned: false,
  onEventsClick: null,
};
